import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api } from "@/lib/api";
import { fmtTL, MONTHS_TR, fmtDateTR, onetimeStatusMeta } from "@/lib/finance";

export default function PendingPayments() {
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    const { data: d } = await api.get("/finance/pending-payments", { params: { workspace } });
    setData(d);
  })(); }, [workspace]);

  const Section = ({ title, rows, columns, empty, testId }) => (
    <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-6" data-testid={testId}>
      <div className="px-6 py-4 border-b border-slate-100 font-semibold text-slate-900">{title}</div>
      {rows?.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">{empty}</div>
      ) : (
        <table className="w-full">
          <thead><tr className="border-b border-slate-100 bg-slate-50/40">
            {columns.map((c) => <th key={c.k} className={`py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.h}</th>)}
          </tr></thead>
          <tbody>{rows?.map((r, i) => (
            <tr key={r.id || i} className="border-b border-slate-50 last:border-none">
              {columns.map((c) => <td key={c.k} className={`py-3 px-6 text-sm ${c.align === "right" ? "text-right font-semibold text-slate-900" : "text-slate-700"}`}>{c.render ? c.render(r) : r[c.k]}</td>)}
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-center gap-3">
        <Link to="/finance" className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ArrowLeft size={16} /></Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Bekleyen Ödemeler</h1>
          <p className="text-sm text-slate-500 mt-1">Ödeme günü geçmiş ve tamamlanmamış tüm ödemeler.</p>
        </div>
        {data && (
          <div className="ml-auto rounded-xl bg-orange-50 border border-orange-100 px-5 py-3">
            <div className="text-xs text-slate-500">Toplam Bekleyen</div>
            <div className="text-xl font-bold text-orange-600" data-testid="pp-total">{fmtTL(data.total)}</div>
          </div>
        )}
      </div>

      {data && (
        <>
          <Section title="Bu Ay — Ödeme Günü Geçmiş" rows={data.current} testId="pp-current"
            empty="Bu ay için bekleyen ödeme yok."
            columns={[
              { k: "brand_name", h: "Marka" },
              { k: "month", h: "Dönem", render: (r) => `${MONTHS_TR[r.month - 1]} ${r.year}` },
              { k: "payment_day", h: "Ödeme Günü" },
              { k: "amount", h: "Tutar", align: "right", render: (r) => fmtTL(r.amount) },
              { k: "status", h: "Durum" },
            ]} />
          <Section title="Önceki Aylardan Kalan — Ödemedi" rows={data.past} testId="pp-past"
            empty="Geçmiş dönem borcu yok."
            columns={[
              { k: "brand_name", h: "Marka" },
              { k: "month", h: "Dönem", render: (r) => `${MONTHS_TR[r.month - 1]} ${r.year}` },
              { k: "amount", h: "Tutar", align: "right", render: (r) => fmtTL(r.amount) },
              { k: "note", h: "Not" },
            ]} />
          <Section title="Tek Seferlik — Ödeme Bekleniyor" rows={data.onetime} testId="pp-onetime"
            empty="Bekleyen tek seferlik ödeme yok."
            columns={[
              { k: "brand_name", h: "Marka" },
              { k: "date", h: "Tarih", render: (r) => fmtDateTR(r.date) },
              { k: "amount", h: "Tutar", align: "right", render: (r) => fmtTL(r.amount) },
              { k: "status", h: "Durum", render: (r) => onetimeStatusMeta(r.status)?.label || r.status },
            ]} />
        </>
      )}
    </FinanceLayout>
  );
}
